import {
  MLB_SIMILARITY_MIN_INNINGS_PITCHED,
  MLB_SIMILARITY_MIN_PLATE_APPEARANCES,
} from "../constants/mlbSimilarity";

const MLB_SIMILAR_ROLES = new Set(["hitting", "pitching"]);

const firstPresent = (record = {}, keys = []) => {
  for (const key of keys) {
    if (record[key] !== undefined && record[key] !== null && record[key] !== "") {
      return record[key];
    }
  }
  return null;
};

const parseNumericStat = (value) => {
  if (value === null || value === undefined) return null;
  const normalized = String(value).trim().replace(/,/g, "");
  if (normalized === "") return null;
  const parsed = Number(normalized);
  return Number.isFinite(parsed) ? parsed : null;
};

const parseBaseballInnings = (value) => {
  if (value === null || value === undefined) return null;
  const normalized = String(value).trim().replace(/,/g, "");
  if (normalized === "") return null;

  const [wholeRaw, outsRaw = "0", extra] = normalized.split(".");
  if (extra !== undefined || wholeRaw === "" || outsRaw === "") return null;

  const whole = Number(wholeRaw);
  const outs = Number(outsRaw);
  if (
    !Number.isInteger(whole) ||
    !Number.isInteger(outs) ||
    whole < 0 ||
    outs < 0 ||
    outs > 2
  ) {
    return null;
  }

  return whole + outs / 3;
};

const derivePlateAppearances = (season = {}) => {
  const plateAppearances = parseNumericStat(
    firstPresent(season, ["plateAppearances", "pa"])
  );
  if (plateAppearances != null && plateAppearances > 0) return plateAppearances;

  const atBats = parseNumericStat(firstPresent(season, ["atBat", "ab"]));
  if (atBats == null) return null;

  const walks = parseNumericStat(firstPresent(season, ["baseOnBalls", "bb"])) || 0;
  const hitByPitch =
    parseNumericStat(firstPresent(season, ["hitByPitch", "hbp"])) || 0;
  const sacrificeFlies =
    parseNumericStat(firstPresent(season, ["sacrificeFly", "sf"])) || 0;
  const sacrificeHits =
    parseNumericStat(firstPresent(season, ["sacrificeHit", "sh"])) || 0;

  return atBats + walks + hitByPitch + sacrificeFlies + sacrificeHits;
};

const isEligibleSeason = (season, role) => {
  if (role === "hitting") {
    return derivePlateAppearances(season) >= MLB_SIMILARITY_MIN_PLATE_APPEARANCES;
  }
  if (role === "pitching") {
    const inningsPitched = parseBaseballInnings(
      firstPresent(season, ["inningsPitched", "ip"])
    );
    return inningsPitched >= MLB_SIMILARITY_MIN_INNINGS_PITCHED;
  }
  return false;
};

export const eligibleMlbSimilarSeasons = (player, role) => {
  if (!MLB_SIMILAR_ROLES.has(role)) return [];

  const seasons = player?.stats?.[role]?.seasons || {};
  return Object.entries(seasons)
    .filter(([, season]) => isEligibleSeason(season, role))
    .map(([year]) => String(year))
    .sort((a, b) => Number(b) - Number(a));
};

export const parseMlbSimilarSearch = (searchParams) => {
  const season = searchParams?.get("season") || undefined;
  const roleParam = searchParams?.get("role");
  const role = MLB_SIMILAR_ROLES.has(roleParam) ? roleParam : undefined;
  return { season, role };
};

export const serializeMlbSimilarSearch = ({ season, role } = {}) => {
  const searchParams = new URLSearchParams();
  if (season) searchParams.set("season", season);
  if (MLB_SIMILAR_ROLES.has(role)) searchParams.set("role", role);
  return searchParams;
};
